/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React from 'react';
import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  AuthzEnvState,
  DEFAULT_MODE_LABEL,
  RoutePath,
  RouteSource,
} from './types';

const useStyles = makeStyles(theme => ({
  path: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    wordBreak: 'break-all',
  },
  mgmt: {
    color: theme.palette.warning.main,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  source: { fontSize: '0.75rem', color: theme.palette.text.secondary },
  empty: { padding: theme.spacing(2) },
}));

const sourceLabel = (s: RouteSource): string => {
  if (s.kind === 'additionalPaths') return 'additionalPaths';
  if (s.kind === 'managementPaths') return 'managementPaths';
  return `apiExposed.${s.exposureType}.${s.useCase}`;
};

type Props = { env: AuthzEnvState };

/**
 * The coverage table is the centrepiece of this surface. Once any route
 * selects the Server the proxy stops synthesizing its default route, so
 * anything not listed here is REJECTED at Phase 2. Coverage has to be
 * exhaustive for the port, which is why the inventory is derived from
 * apiExposed rather than hand-written.
 */
export const RouteCoverageTable = ({ env }: Props) => {
  const classes = useStyles();
  const mgmtCallers = env.managementCallers;
  // `callers` mode names an explicit list; the other two modes are postures,
  // not lists, so showing a count for them would be a lie.
  const defaultLabel =
    env.defaultMode === 'callers'
      ? `${env.defaultCallers.length} named`
      : DEFAULT_MODE_LABEL[env.defaultMode];
  const rows: RoutePath[] = [...env.management, ...env.default];

  if (rows.length === 0) {
    return (
      <Alert severity="info" className={classes.empty}>
        No gRPC paths derived for port <code>{env.port}</code>. Phase 1 is still
        fine (it emits only the Server), but Phase 2 would render a bare Server
        with nothing authorized.
      </Alert>
    );
  }

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Path prefix</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Tier</TableCell>
            <TableCell>Authorized by</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(r => (
            <TableRow key={`${r.tier}:${r.path}`}>
              <TableCell className={classes.path}>{r.path}</TableCell>
              <TableCell className={classes.source}>
                {sourceLabel(r.source)}
              </TableCell>
              <TableCell>
                {r.tier === 'management' ? (
                  <span className={classes.mgmt}>management</span>
                ) : (
                  'default'
                )}
              </TableCell>
              <TableCell>
                {r.tier === 'management' ? (
                  <Tooltip
                    title={
                      mgmtCallers.length > 0
                        ? mgmtCallers
                            .map(c => `${c.name}.${c.namespace}`)
                            .join(', ')
                        : 'No managementCallers declared. Phase 2 would render a policy the controller ignores.'
                    }
                  >
                    <span>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={mgmtCallers.length === 0 ? 'secondary' : 'default'}
                        label={
                          mgmtCallers.length === 0
                            ? 'none declared'
                            : `${mgmtCallers.length} caller${
                                mgmtCallers.length === 1 ? '' : 's'
                              }`
                        }
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <Chip size="small" variant="outlined" label={defaultLabel} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography variant="caption" color="textSecondary">
        Adding any route removes the proxy's synthesized default route, which is
        what carries the probe paths. Anything served on <code>{env.port}</code>{' '}
        and missing from this table is rejected once Phase 2 lands.
      </Typography>
    </>
  );
};
